/**
 * Header component with User Menu dropdown (W1 S0b extraction from App.tsx,
 * moved verbatim).
 *
 * W1-T17 (Stream 7 shell closure) adds, ALL gated behind isW1Shell() so
 * flag-OFF renders byte-identical to before this task: the G03 header
 * search control, G04/G05/G06 drawer triggers (Jobs/Approvals/Alerts —
 * JobsDrawer/ApprovalsDrawer/AlertsDrawer are URL-driven and mounted once
 * in Layout.tsx; these buttons only set the query params that open them),
 * the G07 Help trigger (HelpDrawer's open state is lifted to Layout, so
 * onOpenHelp is a prop), and the G08/G11 account-menu redesign (Profile /
 * Security / Effective access / Platform settings, Sign out routed through
 * SignOutConfirm instead of calling logout() directly).
 */
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Bell, Menu, User, Settings, Sliders, LogOut, ChevronDown, Search, Briefcase, CheckSquare, HelpCircle, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import EnvironmentSelector from '../EnvironmentSelector';
import SignOutConfirm from './SignOutConfirm';
import { isW1Shell } from '../../utils/w1Flag';

const Header: React.FC<{ onMenuClick: () => void; onOpenHelp?: () => void }> = ({ onMenuClick, onOpenHelp }) => {
  const { user, logout } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [signOutConfirming, setSignOutConfirming] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const flagOn = isW1Shell();
  const isAdmin = !!user?.groups?.includes('Admins');

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-user-menu]')) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setUserMenuOpen(false);
  }, [location.pathname]);

  const userInitial = user?.email?.charAt(0).toUpperCase() || user?.username?.charAt(0).toUpperCase() || 'U';
  const displayName = user?.email || user?.username || 'User';

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchInput.trim()) navigate(`/search?q=${encodeURIComponent(searchInput.trim())}`);
  };

  const openJobs = () => {
    const next = new URLSearchParams(searchParams);
    next.set('drawer', '1');
    setSearchParams(next);
  };
  const openApprovals = () => {
    const next = new URLSearchParams(searchParams);
    next.set('approvals', '1');
    setSearchParams(next);
  };
  const openAlerts = () => {
    const next = new URLSearchParams(searchParams);
    next.set('panel', 'alerts');
    setSearchParams(next);
  };

  return (
    <header className="h-16 bg-gradient-to-r from-white via-gray-50 to-white border-b border-gray-200 shadow-sm flex items-center justify-between px-4 lg:px-6 gap-4">
      <div className="flex items-center space-x-4">
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
        >
          <Menu className="w-6 h-6 text-gray-600" />
        </button>
      </div>

      {/* G03: header search control → /search. Flag-gated new addition.
          Hidden on /search itself — that page renders its own primary
          search input with the same accessible name; showing both would
          be a confusing duplicate control, not a second real affordance. */}
      {flagOn && location.pathname !== '/search' && (
        <form role="search" onSubmit={submitSearch} className="hidden md:block flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
            <input
              type="text"
              aria-label="Search resources, jobs, help"
              placeholder="Search resources, jobs, help..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-iflow-500"
            />
          </div>
        </form>
      )}

      <div className="flex items-center space-x-3">
        {/* Global environment selector (W1b) */}
        <EnvironmentSelector />

        {/* G04/G05: Jobs and Approvals drawer triggers (Flag-gated new addition). */}
        {flagOn && (
          <>
            <button
              type="button"
              aria-label="Jobs"
              title="Jobs"
              onClick={openJobs}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              <Briefcase className="w-5 h-5 text-gray-600" aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label="Approvals"
              title="Approvals"
              onClick={openApprovals}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              <CheckSquare className="w-5 h-5 text-gray-600" aria-hidden="true" />
            </button>
          </>
        )}

        {/* Notifications — the unread indicator is intentionally NOT rendered
            until it is driven by the real alerts feed (Overview alerts, W3). A
            permanently-lit red dot with no data source is a false signal.
            G06: flag ON opens the real AlertsDrawer; flag OFF is the original
            unwired stub, unchanged. */}
        <button
          type="button"
          aria-label="Notifications"
          title="Notifications"
          onClick={flagOn ? openAlerts : undefined}
          className="relative p-2 rounded-lg hover:bg-gray-100"
        >
          <Bell className="w-5 h-5 text-gray-600" />
        </button>

        {/* G07: Help trigger — HelpDrawer's open state lives in Layout. */}
        {flagOn && onOpenHelp && (
          <button
            type="button"
            aria-label="Help"
            title="Help"
            onClick={onOpenHelp}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            <HelpCircle className="w-5 h-5 text-gray-600" aria-hidden="true" />
          </button>
        )}

        {/* User Menu Dropdown (G08) */}
        <div className="relative" data-user-menu>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center space-x-2 p-1 pr-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-iflow-700 flex items-center justify-center text-white font-medium">
              {userInitial}
            </div>
            <span className="hidden sm:inline text-sm font-medium text-gray-700">{user?.username === 'admin' ? 'Admin' : 'User'}</span>
            <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Menu */}
          {userMenuOpen && (
            <div className="absolute right-0 mt-2 w-64 bg-gray-100 rounded-xl shadow-lg border border-gray-200 py-2 z-50">
              {/* User Info */}
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-iflow-700 flex items-center justify-center text-white font-medium">
                    {userInitial}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.username === 'admin' ? 'Administrator' : (user?.role || 'User')}</p>
                  </div>
                </div>
              </div>

              {!flagOn && (
                // Flag OFF: original menu, byte-for-byte unchanged.
                <>
                  <div className="py-1">
                    <Link to="/profile" onClick={() => setUserMenuOpen(false)} className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                      <User className="w-4 h-4 text-gray-500" />
                      <span>Profile</span>
                    </Link>
                    <Link to="/settings" onClick={() => setUserMenuOpen(false)} className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                      <Settings className="w-4 h-4 text-gray-500" />
                      <span>Settings</span>
                    </Link>
                    <Link to="/preferences" onClick={() => setUserMenuOpen(false)} className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                      <Sliders className="w-4 h-4 text-gray-500" />
                      <span>Preferences</span>
                    </Link>
                  </div>
                  <div className="border-t border-gray-100 py-1">
                    <button onClick={logout} className="flex items-center space-x-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full transition-colors">
                      <LogOut className="w-4 h-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </>
              )}

              {flagOn && (
                // Flag ON: G08 contract — Profile / Security / Effective
                // access separated from Preferences; Platform settings
                // present only for Admins (per "absent and link to
                // Administration only when authorised"); Sign out visually
                // separated and routed through the G11 confirm dialog.
                <>
                  <div className="py-1">
                    <Link to="/profile" onClick={() => setUserMenuOpen(false)} className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                      <User className="w-4 h-4 text-gray-500" />
                      <span>Profile</span>
                    </Link>
                    <Link to="/profile" onClick={() => setUserMenuOpen(false)} className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                      <ShieldCheck className="w-4 h-4 text-gray-500" />
                      <span>Security</span>
                    </Link>
                    <Link to="/preferences" onClick={() => setUserMenuOpen(false)} className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                      <Sliders className="w-4 h-4 text-gray-500" />
                      <span>Preferences</span>
                    </Link>
                    <div className="px-4 py-2 text-xs text-gray-500" data-testid="effective-access">
                      Effective access: {(user?.groups && user.groups.length > 0) ? user.groups.join(', ') : 'no elevated groups'}
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="border-t border-gray-100 py-1">
                      <Link to="/access" onClick={() => setUserMenuOpen(false)} className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                        <Settings className="w-4 h-4 text-gray-500" />
                        <span>Platform settings</span>
                      </Link>
                    </div>
                  )}
                  <div className="border-t border-gray-100 py-1">
                    <button
                      onClick={() => { setUserMenuOpen(false); setSignOutConfirming(true); }}
                      className="flex items-center space-x-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {flagOn && signOutConfirming && (
        <SignOutConfirm onCancel={() => setSignOutConfirming(false)} />
      )}
    </header>
  );
};

export default Header;
